'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Technology {
  id: number;
  name: string;
  category: string;
  icon: string;
  description: string;
}

interface TechnologyStackProps {
  className?: string;
}

const TechnologyStack = ({ className = '' }: TechnologyStackProps) => {
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', 'Frontend', 'Backend', 'Mobile', 'Database', 'Cloud'];

  const technologies: Technology[] = [
    {
      id: 1,
      name: "React",
      category: "Frontend",
      icon: "CodeBracketIcon",
      description: "Modern UI library for building interactive interfaces"
    },
    {
      id: 2,
      name: "Next.js",
      category: "Frontend",
      icon: "CommandLineIcon",
      description: "React framework for production-grade applications"
    },
    {
      id: 3,
      name: "TypeScript",
      category: "Frontend",
      icon: "DocumentTextIcon",
      description: "Typed superset of JavaScript for better code quality"
    },
    {
      id: 4,
      name: "Node.js",
      category: "Backend",
      icon: "ServerIcon",
      description: "JavaScript runtime for scalable server applications"
    },
    {
      id: 5,
      name: "Python",
      category: "Backend",
      icon: "CpuChipIcon",
      description: "Versatile language for backend and AI/ML solutions"
    },
    {
      id: 6,
      name: "React Native",
      category: "Mobile",
      icon: "DevicePhoneMobileIcon",
      description: "Cross-platform mobile app development framework"
    },
    {
      id: 7,
      name: "Flutter",
      category: "Mobile",
      icon: "DeviceTabletIcon",
      description: "Google's UI toolkit for beautiful native apps"
    },
    {
      id: 8,
      name: "MongoDB",
      category: "Database",
      icon: "CircleStackIcon",
      description: "NoSQL database for flexible data storage"
    },
    {
      id: 9,
      name: "PostgreSQL",
      category: "Database",
      icon: "TableCellsIcon",
      description: "Advanced relational database system"
    },
    {
      id: 10,
      name: "AWS",
      category: "Cloud",
      icon: "CloudIcon",
      description: "Comprehensive cloud computing platform"
    },
    {
      id: 11,
      name: "Docker",
      category: "Cloud",
      icon: "CubeIcon",
      description: "Containerization for consistent deployments"
    },
    {
      id: 12,
      name: "Firebase",
      category: "Cloud",
      icon: "BoltIcon",
      description: "Google's platform for mobile and web apps"
    }
  ];

  const filteredTechnologies = activeCategory === 'All'
    ? technologies
    : technologies.filter(tech => tech.category === activeCategory);

  return (
    <section data-component-id="src/app/homepage/components/TechnologyStack.tsx:115:4" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="115" data-component-file="TechnologyStack.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" className={`py-24 bg-surface ${className}`}>
      <div data-component-id="src/app/homepage/components/TechnologyStack.tsx:116:6" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="116" data-component-file="TechnologyStack.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8">
        <div data-component-id="src/app/homepage/components/TechnologyStack.tsx:117:8" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="117" data-component-file="TechnologyStack.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" className="text-center mb-16">
          <h2 data-component-id="src/app/homepage/components/TechnologyStack.tsx:118:10" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="118" data-component-file="TechnologyStack.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20lg%3Atext-5xl%20font-bold%20text-text-primary%20mb-4%22%2C%22textContent%22%3A%22Our%20Technology%20Stack%22%7D" className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Our Technology Stack
          </h2>
          <p data-component-id="src/app/homepage/components/TechnologyStack.tsx:121:10" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="121" data-component-file="TechnologyStack.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20md%3Atext-xl%20text-text-secondary%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22We%20leverage%20cutting-edge%20technologies%20to%20build%20robust%2C%20scalable%2C%20and%20future-proof%20solutions%22%7D" className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto">
            We leverage cutting-edge technologies to build robust, scalable, and future-proof solutions
          </p>
        </div>

        <div data-component-id="src/app/homepage/components/TechnologyStack.tsx:126:8" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="126" data-component-file="TechnologyStack.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20items-center%20justify-center%20gap-3%20mb-12%22%7D" className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button data-component-id="src/app/homepage/components/TechnologyStack.tsx:128:12" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="128" data-component-file="TechnologyStack.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-3 text-sm font-semibold rounded-lg transition-all duration-base ${
                activeCategory === category
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-card text-text-secondary border border-border hover:border-primary hover:text-primary'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div data-component-id="src/app/homepage/components/TechnologyStack.tsx:142:8" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="142" data-component-file="TechnologyStack.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20lg%3Agrid-cols-3%20xl%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTechnologies.map((tech) => (
            <div data-component-id="src/app/homepage/components/TechnologyStack.tsx:144:12" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="144" data-component-file="TechnologyStack.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22group%20bg-card%20border%20border-border%20rounded-lg%20p-6%20hover%3Ashadow-lg%20hover%3A-translate-y-1%20transition-all%20duration-base%22%7D"
              key={tech.id}
              className="group bg-card border border-border rounded-lg p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-base"
            >
              <div data-component-id="src/app/homepage/components/TechnologyStack.tsx:148:14" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="148" data-component-file="TechnologyStack.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-14%20h-14%20bg-gradient-to-br%20from-primary%20to-brand-trust%20rounded-lg%20mb-4%20group-hover%3Ascale-110%20transition-transform%20duration-base%22%7D" className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-brand-trust rounded-lg mb-4 group-hover:scale-110 transition-transform duration-base">
                <Icon data-component-id="src/app/homepage/components/TechnologyStack.tsx:149:16" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="149" data-component-file="TechnologyStack.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-white%22%7D" name={tech.icon as any} size={28} className="text-white" />
              </div>
              <h3 data-component-id="src/app/homepage/components/TechnologyStack.tsx:151:14" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="151" data-component-file="TechnologyStack.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-bold%20text-text-primary%20mb-2%20group-hover%3Atext-primary%20transition-colors%20duration-base%22%7D" className="text-lg font-bold text-text-primary mb-2 group-hover:text-primary transition-colors duration-base">
                {tech.name}
              </h3>
              <p data-component-id="src/app/homepage/components/TechnologyStack.tsx:154:14" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="154" data-component-file="TechnologyStack.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-secondary%20leading-relaxed%22%7D" className="text-sm text-text-secondary leading-relaxed">
                {tech.description}
              </p>
              <span data-component-id="src/app/homepage/components/TechnologyStack.tsx:157:14" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="157" data-component-file="TechnologyStack.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-block%20mt-3%20px-3%20py-1%20text-xs%20font-medium%20text-primary%20bg-primary%2F10%20rounded-full%22%7D" className="inline-block mt-3 px-3 py-1 text-xs font-medium text-primary bg-primary/10 rounded-full">
                {tech.category}
              </span>
            </div>
          ))}
        </div>

        <div data-component-id="src/app/homepage/components/TechnologyStack.tsx:164:8" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="164" data-component-file="TechnologyStack.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-16%20bg-gradient-to-r%20from-primary%20to-brand-trust%20rounded-2xl%20p-8%20md%3Ap-12%20text-center%22%7D" className="mt-16 bg-gradient-to-r from-primary to-brand-trust rounded-2xl p-8 md:p-12 text-center">
          <h3 data-component-id="src/app/homepage/components/TechnologyStack.tsx:165:10" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="165" data-component-file="TechnologyStack.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-2xl%20md%3Atext-3xl%20font-bold%20text-white%20mb-4%22%2C%22textContent%22%3A%22Need%20a%20Custom%20Technology%20Solution%3F%22%7D" className="text-2xl md:text-3xl font-bold text-white mb-4">
            Need a Custom Technology Solution?
          </h3>
          <p data-component-id="src/app/homepage/components/TechnologyStack.tsx:168:10" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="168" data-component-file="TechnologyStack.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-white%20opacity-90%20mb-6%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22We're%20always%20exploring%20new%20technologies%20and%20can%20adapt%20to%20your%20specific%20requirements%22%7D" className="text-lg text-white opacity-90 mb-6 max-w-2xl mx-auto">
            We're always exploring new technologies and can adapt to your specific requirements
          </p>
          <a data-component-id="src/app/homepage/components/TechnologyStack.tsx:171:10" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="171" data-component-file="TechnologyStack.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22%2Fcontact%22%2C%22className%22%3A%22inline-flex%20items-center%20px-8%20py-4%20text-lg%20font-semibold%20text-primary%20bg-white%20hover%3Abg-gray-100%20rounded-lg%20shadow-lg%20hover%3Ashadow-xl%20hover%3A-translate-y-1%20transition-all%20duration-base%22%2C%22textContent%22%3A%22Discuss%20Your%20Project%22%7D"
            href="/contact"
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-primary bg-white hover:bg-gray-100 rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-base"
          >
            Discuss Your Project
            <Icon data-component-id="src/app/homepage/components/TechnologyStack.tsx:176:12" data-component-path="src/app/homepage/components/TechnologyStack.tsx" data-component-line="176" data-component-file="TechnologyStack.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%2C%22className%22%3A%22ml-2%22%7D" name="ArrowRightIcon" size={20} className="ml-2" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default TechnologyStack;